import { Pipe, PipeTransform } from '@angular/core';
import { catchError, map, mergeWith, Observable, of, startWith, takeWhile, tap, timer } from 'rxjs';

@Pipe({
  name: 'trackStatus',
  standalone: true,
})
export class TrackStatusPipe implements PipeTransform {
  private defaultWaitingTime = 200; // most optimal time according to an article;

  transform<T>(
    obs$: Observable<T> | null | undefined,
    options?: { waitingTime: number },
  ): Observable<ObservableStatus<T>> | null | undefined {
    if (!obs$) {
      return obs$;
    }

    const result = {
      value: null,
      isLoading: false,
      isWaiting: false,
      error: null,
    } as ObservableStatus<T>;

    const waitingTime = options?.waitingTime || this.defaultWaitingTime;

    const trackedObs$ = obs$.pipe(
      startWith(result),
      tap(() => {
        result.isLoading = true;
        result.isWaiting = true;
      }),
      tap((value) => {
        if (value === result) return;
        result.value = value as T;
        result.isLoading = false;
        result.isWaiting = false;
      }),
      mergeWith(
        timer(waitingTime).pipe(
          takeWhile(() => result.isLoading),
          tap(() => {
            result.isWaiting = false;
          }),
        ),
      ),
      map(() => ({ ...result })),
      catchError((error) => {
        result.error = error;
        result.isLoading = false;
        result.isWaiting = false;
        return of({ ...result });
      }),
    );

    return trackedObs$;
  }
}

export interface ObservableStatus<T> {
  value: T | null;
  isLoading: boolean;
  isWaiting: boolean;
  error: unknown;
}
